import React, { useEffect, useState } from 'react'
import {
  ActionChip,
  useChipCollection,
  Row,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  TextField,
  Separator,
} from '@fs/zion-ui'
import { ContentLock, ContentCheck, MenuSearch } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { countSupersOfSharedField, getMostRecentFieldValue, useSlsStateContext } from '@fs/zion-sls'
import ChipUnfilteredExpander from './ChipUnfilteredExpander'

const byLabel = (a, b) => {
  if (a.label < b.label) return -1
  if (a.label > b.label) return 1
  return 0
}

/**
 * FieldChipSelector component allows users to select chips from a list of options.
 * It supports searching, locking shared fields, and displaying added fields.
 *
 * @param {Object} props - Component props
 * @param {Array} props.chipData - Data for chips coming from the useTreatedFieldChips hook
 * @param {Array} props.fieldData - Data from the selected form fields
 * @param {Function} props.handleSelection - Callback function when a chip is selected or deselected
 * @param {boolean} [props.allowSelectingMultiple=true] - Flag to allow multiple selections
 * @param {boolean} [props.lockSharedFields=true] - Flag to lock shared fields from being deselected
 * @param {Object} props.unlinkingStates - States for unlinking fields
 * @param {boolean} [props.showSearch=true] - Flag to show or hide the search input
 * @returns {React.Component} The FieldChipSelector component
 */
export default function FieldChipSelector({
  chipData = [],
  fieldData = [],
  handleSelection = () => {},
  allowSelectingMultiple = true,
  lockSharedFields = true,
  unlinkingStates,
  showSearch = true,
}) {
  const state = useSlsStateContext()
  const [searchTerm, setSearchTerm] = useState('')
  const [t] = useTranslation()
  const addedChipsTitle = t('field-chip-selector.added-fields.title', 'Added Fields')
  const lockedChipsTitle = t('field-chip-selector.locked-fields.title', 'Locked Fields')
  const searchFieldsPlaceholder = t('field-chip-selector.search-field.placeholder', 'Search Fields')
  const startingChips = fieldData.map((f) => {
    let value = getMostRecentFieldValue(f) || f.value || ''
    if (typeof value === 'object') {
      value = value.normalizedValues?.[0]?.text || value.origValue?.text
    }
    const nameFields = ['NAME_PRE', 'NAME_GN', 'NAME_SURN', 'NAME_SUF']
    // only add parenthesis after label for most recent field value
    const labelWithValue = `${f.label} ${value && !nameFields.includes(value) ? `(${value})` : ''}`
    const chip = {
      label: value ? labelWithValue : f.label,
      id: f.id,
    }
    if (!chip.label) {
      f.label = f.name
      chip.label = f.name
    }
    const isEditable = f?.typeData?.editable ?? true
    chip.value = `${f.fieldType}:${Math.random()}`
    chip.disabled =
      (countSupersOfSharedField(f, state.images) > 1 &&
        !unlinkingStates?.[f.id]?.state &&
        !unlinkingStates?.[f.fieldType] &&
        lockSharedFields) ||
      !isEditable // We want to disable any field that is not editable
    chip.emphasized = !chip.disabled
    chip.selected = !chip.disabled
    if (chip.disabled) {
      chip.description = t('field-chip-selector.disabled-chip.shared-field', 'Disabled Linked Field')
    }
    return chip
  })

  const chipCollection = useChipCollection(startingChips.filter((c) => !c.disabled).sort(byLabel))
  const { addChip, chips } = chipCollection

  const lockedChipCollection = useChipCollection(
    startingChips
      .filter((c) => c.disabled)
      .sort(byLabel)
      .map((c) => ({
        ...c,
        Icon: ContentLock,
      }))
  )

  useEffect(() => {
    handleSelection(chips, lockedChipCollection.chips)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chips, lockedChipCollection.chips])

  const handleSearch = (e) => setSearchTerm(e.target.value)
  const handleClearSearch = () => setSearchTerm('')
  const chipContainsSearch = (chipLabel) =>
    !searchTerm ||
    (chipLabel.toLowerCase()[0] === searchTerm.toLowerCase()[0] &&
      chipLabel.toLowerCase().includes(searchTerm.toLowerCase())) ||
    chipLabel.toLowerCase().includes(` ${searchTerm.toLowerCase()}`)

  const filteredChipData = chipData.map((set) => ({
    ...set,
    data: set?.data?.filter((item) => chipContainsSearch(item.label)),
  }))

  const hasNoFields = filteredChipData?.every((set) => set?.data?.length === 0)
  return (
    <>
      {showSearch && (
        <>
          <TextField
            placeholder={searchFieldsPlaceholder}
            Icon={MenuSearch}
            value={searchTerm}
            onChange={handleSearch}
            clearable
            onClear={handleClearSearch}
            label={false}
            aria-label={searchFieldsPlaceholder}
          />
          <Separator />
        </>
      )}
      {!searchTerm && chips.length > 0 && (
        <ChipUnfilteredExpander headerText={addedChipsTitle} chipCollection={chipCollection} />
      )}
      <Separator />
      {!searchTerm && lockedChipCollection.chips.length > 0 && (
        <ChipUnfilteredExpander headerText={lockedChipsTitle} chipCollection={lockedChipCollection} />
      )}
      {hasNoFields ? (
        <p>
          {t(
            'field-chip-selector.find-a-field.no-matches',
            'Hmm, no matches. To find a field, try different keywords or search through the list without the search'
          )}
        </p>
      ) : (
        filteredChipData?.map((set) => (
          <React.Fragment key={set.key}>
            {set?.data?.length > 0 && (
              <>
                <Separator />
                <Expander initiallyOpen>
                  <ExpanderHeader>{set.title}</ExpanderHeader>
                  <Separator />
                  <ExpanderContent>
                    <Row wrap gutters="xxs">
                      {set.data.map((item) => (
                        <SelectableActionChip
                          item={item}
                          allowSelectingMultiple={allowSelectingMultiple}
                          addChip={addChip}
                          key={`${set.key}-${item.value}`}
                        />
                      ))}
                    </Row>
                  </ExpanderContent>
                </Expander>
              </>
            )}
          </React.Fragment>
        ))
      )}
    </>
  )
}

// This is done because, the chip occasionally gets extra properties passed in via the item prop (like elementTypes for example) and spreading those propse onto ActionChip causes a warning in the console
// This way, we can pass any props we want into the SelectableActionChip, and only pass ActionChip the props that ActionChip actually uses
const validActionChipProps = [
  'avatarProps',
  'centered',
  'color',
  'debounce',
  'description',
  'disabled',
  'emphasis',
  'external',
  'icon',
  'inline',
  'label',
  'selected',
  'key',
  'linkName',
  'onClick',
  'target',
  'to',
  'value',
]
function SelectableActionChip({ item, allowSelectingMultiple, addChip }) {
  const [t] = useTranslation()
  const [selected, setSelected] = useState(false)
  const [label, setLabel] = useState(item.label)
  const chipAddedLabel = t('field-chip-selector.addional-fields.added-label', 'Added!')

  // this ref is used to store the timeout ids for clearing them on unmount
  const timeoutRef = React.useRef([])
  const chipAdded = () => {
    setSelected(true)
    setLabel(chipAddedLabel)
    timeoutRef.current.push(
      setTimeout(() => {
        setSelected(false)
        setLabel(item.label)
      }, 2000)
    )
  }

  // Clear timeouts on unmount to avoid memory leaks
  const clearTimeouts = () => timeoutRef.current.forEach((timeout) => clearTimeout(timeout))
  useEffect(() => () => clearTimeouts(), [])

  const filteredActionChipProps = Object.fromEntries(
    Object.entries(item).filter(([key]) => validActionChipProps.includes(key))
  )

  return (
    <ActionChip
      {...filteredActionChipProps}
      selected={selected}
      label={label}
      Icon={selected ? ContentCheck : undefined}
      onClick={() => {
        // Values must be unique to be added - they are changed later in EditIndex: handleChangedFields()
        addChip({
          ...item,
          emphasized: true,
          selected: true,
          value: allowSelectingMultiple ? `${item.value || ''}:${Math.random()}` : item.value || '',
        })
        chipAdded()
      }}
    />
  )
}
